<script setup>
import { computed } from 'vue';
import IconHeaderClose from '@/components/svg/IconHeaderClose.vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  langMode: {
    type: String,
    default: 'CHNKOR',
  },
  colorMode: {
    type: String,
    default: 'fntcolor01',
  },
  sortMode: {
    type: String,
    default: 'desc',
  },
  formData: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits([
  'update:modelValue',
  'changeLang',
  'changeColor',
  'changeSort',
  'update:formData',
]);

const isOpen = computed(() => props.modelValue);

const close = () => {
  emit('update:modelValue', false);
};

// formData 업데이트 헬퍼
const updateFormData = (key, value) => {
  emit('update:formData', {
    ...props.formData,
    [key]: value,
  });
};

// 언어 선택
const langOptions = [
  { value: 'CHNKOR', label: '한자/한글' },
  { value: 'CHN', label: '한자' },
  { value: 'KOR', label: '한글' },
];

// 테마 선택
const colorOptions = [
  { value: 'fntcolor01', label: '배경컬러' },
  { value: 'fntcolor02', label: '글자컬러' },
  { value: 'fntcolor03', label: '글자흑백' },
];

// 좌우순서
const sortOptions = [
  { value: 'asc', label: '년월일시' },
  { value: 'desc', label: '시일월년' },
];

// 시간적용
const timeOptions = [
  { key: 'option1', label: '야자시/조자시' },
  { key: 'option4', label: '한국시 적용' },
];

// 절기 선택
const seasonOptions = [
  { value: false, label: '입춘' },
  { value: true, label: '동지' },
  { value: null, label: '모름' },
];
</script>

<template>
  <Teleport to="body">
    <transition name="drawer-fade">
      <div
        v-if="isOpen"
        class="mobile-option-drawer-overlay"
        @click="close"
      />
    </transition>
    <transition name="drawer-slide">
      <div
        v-if="isOpen"
        class="mobile-option-drawer"
      >
        <!-- 헤더 -->
        <div class="mobile-option-drawer__header">
          <span class="mobile-option-drawer__title">만세력 옵션</span>
          <button class="mobile-option-drawer__close" @click="close">
            <IconHeaderClose />
          </button>
        </div>

        <!-- 옵션 내용 -->
        <div class="mobile-option-drawer__body">
          <!-- 언어 선택 -->
          <div class="drawer-option-section">
            <div class="drawer-option-section__title">언어 선택</div>
            <div class="drawer-option-section__row triple">
              <button
                v-for="opt in langOptions"
                :key="opt.value"
                class="drawer-option-box"
                :class="{ active: langMode === opt.value }"
                @click="emit('changeLang', opt.value)"
              >
                <div class="drawer-option-box__icon lang-icon" :class="opt.value">
                  <template v-if="opt.value === 'CHNKOR'">
                    <span class="chn-char">文</span>
                    <span class="divider">/</span>
                    <span class="kor-char">가</span>
                  </template>
                  <template v-else-if="opt.value === 'CHN'">
                    <span class="chn-char single">文</span>
                  </template>
                  <template v-else>
                    <span class="kor-char single">가</span>
                  </template>
                </div>
                <!-- 체크 뱃지 -->
                <span v-if="langMode === opt.value" class="drawer-check-badge">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17L4 12" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <div class="drawer-option-box__label">{{ opt.label }}</div>
              </button>
            </div>
          </div>

          <!-- 테마 선택 -->
          <div class="drawer-option-section">
            <div class="drawer-option-section__title">테마 선택</div>
            <div class="drawer-option-section__row triple">
              <button
                v-for="opt in colorOptions"
                :key="opt.value"
                class="drawer-option-box"
                :class="{ active: colorMode === opt.value }"
                @click="emit('changeColor', opt.value)"
              >
                <div class="drawer-option-box__icon theme-icon" :class="opt.value">
                  <span class="theme-sample">甲</span>
                </div>
                <span v-if="colorMode === opt.value" class="drawer-check-badge">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17L4 12" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <div class="drawer-option-box__label">{{ opt.label }}</div>
              </button>
            </div>
          </div>

          <!-- 좌우순서 -->
          <div class="drawer-option-section">
            <div class="drawer-option-section__title">좌우순서</div>
            <div class="drawer-option-section__row double">
              <button
                v-for="opt in sortOptions"
                :key="opt.value"
                class="drawer-option-box text-only"
                :class="{ active: sortMode === opt.value }"
                @click="emit('changeSort', opt.value)"
              >
                <div class="drawer-option-box__text-icon">{{ opt.label }}</div>
                <span v-if="sortMode === opt.value" class="drawer-check-badge">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17L4 12" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </button>
            </div>
          </div>

          <!-- 구분선 -->
          <div class="drawer-divider" />

          <!-- 시간적용 -->
          <div class="drawer-option-section">
            <div class="drawer-option-section__title">시간적용</div>
            <div class="drawer-option-section__row double">
              <button
                v-for="opt in timeOptions"
                :key="opt.key"
                class="drawer-option-box text-only"
                :class="{ active: formData[opt.key] }"
                @click="updateFormData(opt.key, !formData[opt.key])"
              >
                <div class="drawer-option-box__text-icon">{{ opt.label }}</div>
                <span v-if="formData[opt.key]" class="drawer-check-badge">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17L4 12" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </button>
            </div>
          </div>

          <!-- 절기 선택 -->
          <div class="drawer-option-section">
            <div class="drawer-option-section__title">절기 선택</div>
            <div class="drawer-option-section__row triple">
              <button
                v-for="opt in seasonOptions"
                :key="String(opt.value)"
                class="drawer-option-box text-only"
                :class="{ active: formData.option2 === opt.value }"
                @click="updateFormData('option2', opt.value)"
              >
                <div class="drawer-option-box__text-icon">{{ opt.label }}</div>
                <span v-if="formData.option2 === opt.value" class="drawer-check-badge">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17L4 12" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style lang="scss" scoped>
// 딤 오버레이
.mobile-option-drawer-overlay {
  display: none;
}

// 드로어 패널
.mobile-option-drawer {
  display: none;
}

@media (max-width: 768px) {
  .mobile-option-drawer-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }

  .mobile-option-drawer {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    padding-top: var(--safe-area-top, 0px);
    width: 306px;
    max-width: 85vw;
    background: #fff;
    z-index: 1001;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 52px;
      padding: 0 16px;
      border-bottom: 1px solid #ebebeb;
    }

    &__title {
      font-family: Pretendard, sans-serif;
      font-size: 16px;
      font-weight: 600;
      color: #333;
      line-height: 22px;
    }

    &__close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
    }

    &__body {
      flex: 1;
      overflow-y: auto;
      padding: 12px 16px 28px;
      display: flex;
      flex-direction: column;
      gap: 28px;
    }
  }

  // 옵션 섹션
  .drawer-option-section {
    &__title {
      font-family: Pretendard, sans-serif;
      font-size: 16px;
      font-weight: 700;
      color: #333;
      line-height: 20px;
      margin-bottom: 12px;
    }

    &__row {
      display: flex;
      gap: 8px;

      &.triple .drawer-option-box {
        width: 86px;
      }

      &.double .drawer-option-box {
        flex: 1;
      }
    }
  }

  // 옵션 박스
  .drawer-option-box {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;

    &__icon {
      width: 86px;
      height: 48px;
      border-radius: 8px;
      border: 1px solid #ebebeb;
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__text-icon {
      width: 100%;
      height: 48px;
      border-radius: 8px;
      border: 1px solid #ebebeb;
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: Pretendard, sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #333;
      line-height: 18px;
    }

    &__label {
      font-family: Pretendard, sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #333;
      text-align: center;
      line-height: 18px;
      margin-top: 6px;
    }

    &.active {
      .drawer-option-box__icon,
      .drawer-option-box__text-icon {
        border: 1px solid #eb4c10;
      }
    }
  }

  // 체크 뱃지
  .drawer-check-badge {
    position: absolute;
    top: 0;
    right: 0;
    width: 18px;
    height: 18px;
    border-radius: 6px;
    background: #eb4c10;
    display: flex;
    align-items: center;
    justify-content: center;

    .text-only & {
      // text-only 박스에서는 우상단
    }
  }

  // 언어 아이콘
  .lang-icon {
    gap: 2px;

    .chn-char {
      font-family: serif;
      font-size: 14px;
      font-weight: 700;
      color: #333;

      &.single {
        font-size: 18px;
      }
    }

    .divider {
      font-size: 12px;
      color: #c2c2c2;
      transform: rotate(30deg);
      display: inline-block;
    }

    .kor-char {
      font-family: Pretendard, sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #333;

      &.single {
        font-size: 18px;
      }
    }
  }

  // 테마 아이콘
  .theme-icon {
    .theme-sample {
      font-family: serif;
      font-size: 20px;
      font-weight: 700;
      line-height: 1;
    }

    &.fntcolor01 {
      background: #5e9f1c;
      border-color: #5e9f1c !important;

      .theme-sample {
        color: #fff;
      }
    }

    &.fntcolor02 {
      background: #fff;

      .theme-sample {
        color: #5e9f1c;
      }
    }

    &.fntcolor03 {
      background: #fff;

      .theme-sample {
        color: #333;
      }
    }
  }

  .drawer-option-box.active .theme-icon {
    &.fntcolor01 {
      border-color: #eb4c10 !important;
    }
  }

  // 구분선
  .drawer-divider {
    width: 100%;
    height: 1px;
    background: #ebebeb;
  }

  // 트랜지션
  .drawer-fade-enter-active,
  .drawer-fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .drawer-fade-enter-from,
  .drawer-fade-leave-to {
    opacity: 0;
  }

  .drawer-slide-enter-active,
  .drawer-slide-leave-active {
    transition: transform 0.3s ease;
  }

  .drawer-slide-enter-from,
  .drawer-slide-leave-to {
    transform: translateX(-100%);
  }
}
</style>
